import { useStore, useGetIdentity } from 'ra-core';
import type { Identite } from './authentification';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA STRUCTURE COURANTE — F-55, ET C'EST CE QUI ÉVITE DEUX FORMES PAR ÉCRAN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-55, et la phrase porte son propre motif : « **le compte qui voit tout travaille toujours
 * DANS une structure courante**, sauf sur ses deux surfaces propres : sa vue de tous les
 * comptes, et son écran de chiffres réservé. **Sans structure courante, chaque écran
 * porterait deux formes, une filtrée et une non** — et c'est exactement ce qui fait qu'une
 * matrice a une ligne par ressource au lieu de deux. »
 *
 * Et l'autre moitié : « **la personne qui tient un espace n'a pas de sélecteur**, portant au
 * plus un rattachement : il n'aurait qu'une ligne. »
 *
 * D'où la règle de ce fichier, en une phrase : **pour un `orgadmin`, la structure courante
 * EST son rattachement et ne se choisit pas ; pour le compte qui voit tout, elle se choisit
 * et rien n'est filtré avant qu'elle le soit.**
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CE FICHIER VIT DANS `fournisseurs/` ET NON DANS `app/`.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La barrière de couches interdit à une ressource d'importer `app/` : `app/` compose, il ne
 * se consomme pas. Or **les écrans de deux ressources ont besoin de cette valeur** — les
 * comptes et les cours sont portés par une structure —, et le sélecteur qui l'écrit vit,
 * lui, dans `app/` puisque F-55 le veut « permanent en tête de la surface ».
 *
 * Le mettre ici tient parce que ce dossier a une définition précise : **ce qui parle à
 * `ra-core` et à l'API, et rien à la peau**. Ce fichier ne parle qu'à `ra-core` — son
 * magasin et son identité. Il n'importe aucun composant, et la barrière le vérifie.
 *
 * **Ce n'est pas de la donnée serveur recopiée**, et la distinction est celle de
 * `DOCTRINE.md` §6 : ce qui est retenu est **un choix de la personne**, pas une réponse
 * d'API. La liste des structures, elle, reste chez `ra-core` et son cache.
 */

/** La clef du magasin. `localStorageStore` de la peau la fait survivre au rechargement. */
const CLEF = 'swype.structureCourante';

export type StructureCourante = {
  /** L'identifiant de la structure de travail, ou `null` s'il n'y en a pas encore. */
  readonly structureId: string | null;
  /** Vrai quand la personne peut en changer : le compte qui voit tout, lui seul. */
  readonly selecteurAffiche: boolean;
  /** Poser la structure courante. Sans effet pour un `orgadmin`, qui n'en a qu'une. */
  readonly choisir: (structureId: string) => void;
};

export function useStructureCourante(): StructureCourante {
  const { identity } = useGetIdentity();
  const [choisie, setChoisie] = useStore<string | null>(CLEF, null);
  const identite = identity as Identite | undefined;

  // **Un `orgadmin` n'a pas de choix, il a un rattachement.** Le lire du magasin lui
  // laisserait la possibilité d'y écrire un autre identifiant — que le serveur refuserait,
  // mais qui produirait un écran d'erreur là où il devrait y avoir son espace.
  if (identite?.role === 'ORGADMIN') {
    return {
      structureId: identite.structureDeRattachement,
      selecteurAffiche: false,
      choisir: () => {
        /* sans objet : au plus un rattachement, Q534 */
      },
    };
  }

  return {
    structureId: choisie,
    selecteurAffiche: identite?.role === 'SUPERADMIN',
    choisir: (structureId: string) => setChoisie(structureId),
  };
}
